import { useEffect, useRef, useState } from 'react';
import type { Message, PendingPromptData, Session } from '../../types';
import { Button } from '../../components/Button';
import { api } from '../../lib/api';
import { Icon } from '../../components/Icon';
import { MessageBubble } from '../../components/MessageBubble';
import { PendingPrompt } from '../../components/PendingPrompt';
import { popTitle, usePopAvatars, usePopProfiles } from '../../lib/usePopAvatars';
import { Select } from '../../components/Select';
import { useStore } from '../../store/StoreProvider';
import { faceOf } from '../control/faces';
import { Crew, Working, useCrew } from './Crew';

export interface ThreadProps {
  session: Session;
  messages: Message[];
  pending: PendingPromptData | null;
  answering: boolean;
  onAnswer: (choice: string) => void;
  /** `profile`: il pop a cui si scrive; un messaggio mentre il team lavora lo ferma e lui risponde */
  onSend: (text: string, profile?: string) => void;
  onFocusNode: (id: string) => void;
  onReopen: () => void;
  /** pannello Piano/Traccia/File chiuso: la chat prende tutta la larghezza */
  wide?: boolean;
}

export function Thread(p: ThreadProps) {
  const [draft, setDraft] = useState('');
  const avatarOf = usePopAvatars();
  const profiles = usePopProfiles();
  const { state, toast } = useStore();
  const [to, setTo] = useState('');
  const tasks = state.tasks[p.session.id];
  const crew = useCrew(p.session, tasks, state.trace);
  // di partenza si scrive al pop della Conversazione; in una sessione con piano al coordinatore
  // (2026-11-02: in una Conversazione con Nico il messaggio andava al PM)
  const own =
    p.session.mode === 'chat' && p.session.chatProfile
      ? profiles.find((x) => x.id === p.session.chatProfile)
      : undefined;
  const talker = profiles.find((x) => x.id === to) ?? own ?? profiles.find((x) => x.coordinator);
  // con una domanda aperta la casella risponde al pop; altrimenti scrive al pop scelto, e se il
  // team sta lavorando il lavoro si ferma finché lui non ha risposto
  const send = () => {
    const text = draft.trim();
    if (!text) return;
    if (p.pending) p.onAnswer(text);
    else p.onSend(text, talker?.id);
    setDraft('');
  };
  const [news, setNews] = useState(0);
  const [pinned, setPinned] = useState(true);
  const scroller = useRef<HTMLDivElement>(null);
  const count = useRef(p.messages.length);

  useEffect(() => {
    const added = p.messages.length - count.current;
    count.current = p.messages.length;
    if (added <= 0) return;
    if (pinned) {
      const el = scroller.current;
      if (el) el.scrollTop = el.scrollHeight;
    } else setNews((n) => n + added);
  }, [p.messages.length, pinned]);

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [p.session.id]);

  const closed = ['completed', 'cancelled', 'failed'].includes(p.session.state);
  const [resuming, setResuming] = useState(false);
  const resume = () => {
    setResuming(true);
    api
      .resume(p.session.id)
      .catch((e: Error) => toast({ kind: 'danger', title: 'Non ripresa', body: e.message }))
      .finally(() => setResuming(false));
  };

  return (
    <div
      style={{
        width: p.wide ? 'auto' : 380,
        flex: p.wide ? 1 : 'none',
        minWidth: 0,
        background: 'var(--panel)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Crew crew={crew} tasks={tasks} profiles={profiles} />
      <div
        ref={scroller}
        onScroll={() => {
          const el = scroller.current;
          if (!el) return;
          const at = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          setPinned(at);
          if (at) setNews(0);
        }}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {p.session.state === 'failed' && (
          <div
            style={{
              background: 'var(--danger-soft)',
              border: '1px solid var(--danger)',
              borderRadius: 'var(--r-lg)',
              padding: 12,
            }}
          >
            <div className="t-strong" style={{ marginBottom: 4 }}>
              La sessione si è fermata.
            </div>
            <div className="t-caption dim">Il motivo è nell’ultimo messaggio del thread.</div>
          </div>
        )}
        {p.messages.map((m) => (
          <MessageBubble
            key={m.id}
            m={m}
            laneColor={p.session.color}
            onFocusNode={p.onFocusNode}
            author={
              m.authorLabel
                ? popTitle(
                    m.authorLabel,
                    profiles,
                    tasks?.find((t) => t.agent === m.authorLabel)?.role,
                  )
                : undefined
            }
            avatar={
              avatarOf(m.authorLabel) ??
              (m.authorLabel ? faceOf(m.authorLabel, tasks, profiles) : null)
            }
          />
        ))}
        {!closed && <Working crew={crew} tasks={tasks} profiles={profiles} />}
        {news > 0 && (
          <button
            onClick={() => {
              const el = scroller.current;
              if (el) el.scrollTop = el.scrollHeight;
              setNews(0);
              setPinned(true);
            }}
            style={{
              position: 'sticky',
              bottom: 0,
              alignSelf: 'center',
              height: 24,
              padding: '0 10px',
              border: 'none',
              borderRadius: 'var(--r-full)',
              background: 'var(--accent)',
              color: 'var(--accent-ink)',
              font: '500 12px/16px var(--font-ui)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Icon name="arrow-down" size={12} />
            {news} {news === 1 ? 'nuovo messaggio' : 'nuovi messaggi'}
          </button>
        )}
      </div>

      {/* la domanda è già nel thread: qui restano solo le scelte a pulsante, se ci sono; la
          risposta libera si scrive nella casella in basso */}
      {p.pending && (p.pending.options?.length ?? 0) > 0 && (
        <div style={{ flex: 'none', padding: '0 16px 12px' }}>
          <PendingPrompt p={p.pending} onAnswer={p.onAnswer} busy={p.answering} freeText={false} />
        </div>
      )}

      <div
        style={{
          flex: 'none',
          borderTop: '1px solid var(--border)',
          padding: '12px 16px',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        {state.role === 'viewer' ? (
          <span className="t-caption faint" style={{ flex: 1 }}>
            Sola lettura: il tuo profilo è da visitatore.
          </span>
        ) : closed ? (
          <>
            <span className="t-caption faint" style={{ flex: 1 }}>
              Sessione conclusa il {new Date(p.session.updatedAt).toLocaleDateString('it-IT')} alle{' '}
              {new Date(p.session.updatedAt).toLocaleTimeString('it-IT', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {/* una sessione fermata o annullata riparte dal lavoro fermo (2026-11-02) */}
            {['failed', 'cancelled'].includes(p.session.state) && p.session.mode !== 'chat' && (
              <Button size="sm" variant="primary" onClick={resume} disabled={resuming}>
                Riprendi
              </Button>
            )}
            <Button size="sm" onClick={p.onReopen}>
              Riapri come nuova richiesta
            </Button>
          </>
        ) : (
          <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: 8 }}>
            {!p.pending && profiles.length > 0 && (
              <div className="thread-to">
                <span className="t-caption dim">Scrivi a</span>
                <Select
                  label="Pop a cui scrivi"
                  value={talker?.id ?? ''}
                  width={190}
                  options={profiles.map((x) => ({
                    value: x.id,
                    label: x.nickname ? `${x.nickname} · ${x.label}` : x.label,
                  }))}
                  onChange={setTo}
                />
                {p.session.state === 'running' && (
                  <span className="t-caption faint">il lavoro si ferma finché non ti risponde</span>
                )}
              </div>
            )}
            <form
              className="cv-composer cv-composer-inline"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) send();
              }}
            >
              <textarea
                value={draft}
                rows={2}
                aria-label={p.pending ? 'Rispondi al pop' : 'Scrivi'}
                placeholder={
                  p.pending
                    ? 'Rispondi al pop…'
                    : `Scrivi a ${talker?.nickname || talker?.label || 'il pop'}…`
                }
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (draft.trim()) send();
                  }
                }}
              />
              <Button
                variant="primary"
                icon="send"
                type="submit"
                aria-label="Invia"
                disabled={!draft.trim() || p.answering}
              />
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
